import React, { useState, useEffect } from 'react';
import { useAppData } from './context/DataContext';
import axios from 'axios';
import { ReactComponent as GoogleLogo } from './GoogleLogo.svg';
import ForgotPassword from "./ForgotPassword.js";
import { Modal, Alert, Divider, ConfigProvider, Button, Checkbox, notification } from 'antd';
import { InputOTP } from 'antd-input-otp';
import { useNavigate } from 'react-router-dom';
import {
    parsePhoneNumberFromString,
    isValidPhoneNumber,
    AsYouType,
    getExampleNumber,
} from 'libphonenumber-js';
import examples from 'libphonenumber-js/examples.mobile.json';

const secrets = require('./secrets.js');
const api = secrets.server_url;

// Default country for phone formatting
const DEFAULT_COUNTRY = 'US';

// Helper function to format phone numbers as the user types
const formatPhoneNumber = (value, country = DEFAULT_COUNTRY) => {
    const formatter = new AsYouType(country);
    return formatter.input(value);
};

// Validates a phone number using libphonenumber-js
const validatePhoneNumber = (phoneNumber, country = DEFAULT_COUNTRY) => {
    try {
        if (phoneNumber.startsWith('+')) {
            return isValidPhoneNumber(phoneNumber);
        }
        return isValidPhoneNumber(phoneNumber, country);
    } catch (error) {
        return false;
    }
};

// Standardizes the phone number into E.164 format (if valid)
const standardizePhoneNumber = (phoneNumber, country = DEFAULT_COUNTRY) => {
    try {
        let parsedNumber;
        if (phoneNumber.startsWith('+')) {
            parsedNumber = parsePhoneNumberFromString(phoneNumber);
        } else {
            parsedNumber = parsePhoneNumberFromString(phoneNumber, country);
        }
        if (parsedNumber && parsedNumber.isValid()) {
            return parsedNumber.format('E.164');
        }
    } catch (error) {
        console.error('Error standardizing phone number:', error);
    }
    return phoneNumber;
};

// Gets an example formatted phone number for placeholder display
const getPlaceholderNumber = (country = DEFAULT_COUNTRY) => {
    try {
        const example = getExampleNumber(country, examples);
        return example ? example.formatNational() : '(123) 456-7890';
    } catch (error) {
        return '(123) 456-7890';
    }
};

// OTP input screen component
function OTPInputScreen({ otpType, otpSentTo, otpValue, setOtpValue, otpError, otpMessage, verifying, resendTimer, handleOTPGoBack, handleVerifyOTP, handleResendOTP }) {
    return (
        <div className="otp-container">
            <div className="otp-header">
                <Button
                    type="text"
                    className="back-button"
                    onClick={handleOTPGoBack}
                    style={{ marginBottom: '16px' }}
                    aria-label="Go back"
                >
                    &larr; Back
                </Button>
                <h3>Verification Code</h3>
                {otpMessage ? (
                    <p>{otpMessage}</p>
                ) : (
                    <p>
                        We've sent a verification code to{' '}
                        <strong>{otpType === 'phone' ? 'phone' : 'email'}</strong>: {' '}
                        <strong>{otpSentTo}</strong>
                    </p>
                )}
            </div>
            <div className="otp-inputs" style={{ margin: '24px 0' }}>
                <label
                    htmlFor="otp-input"
                    style={{
                        display: 'block',
                        textAlign: 'center',
                        marginBottom: '12px',
                        fontWeight: '500'
                    }}
                >
                    Enter 6-digit verification code
                </label>
                <div
                    style={{
                        marginBottom: '20px',
                        display: 'flex',
                        justifyContent: 'center'
                    }}
                >
                    <InputOTP
                        length={6}
                        inputType="numeric"
                        autoFocus={true}
                        onChange={setOtpValue}
                        value={otpValue}
                        autoSubmit={handleVerifyOTP}
                    />
                </div>
                {otpError && (
                    <p
                        role="alert"
                        aria-live="assertive"
                        style={{
                            color: '#ff4d4f',
                            margin: '8px 0',
                            textAlign: 'center',
                            fontSize: '14px'
                        }}
                    >
                        {otpError}
                    </p>
                )}
            </div>
            <div className="otp-actions" style={{ textAlign: 'center' }}>
                <Button
                    type="primary"
                    onClick={() => handleVerifyOTP(otpValue)}
                    disabled={
                        !otpValue ||
                        !Array.isArray(otpValue) ||
                        otpValue.length !== 6 ||
                        otpValue.includes('') ||
                        verifying
                    }
                    loading={verifying}
                    style={{
                        minWidth: '120px',
                        marginBottom: '16px',
                        height: '40px',
                        fontWeight: '500',
                        borderRadius: '6px'
                    }}
                >
                    {verifying ? 'Verifying...' : 'Verify'}
                </Button>
                <div className="resend-container">
                    <p style={{ fontSize: '14px', color: '#888' }}>
                        Didn't receive the code?{' '}
                        {resendTimer > 0 ? (
                            <span>Resend in <strong>{resendTimer}s</strong></span>
                        ) : resendTimer === -1 ? (
                            <span>Sending...</span>
                        ) : (
                            <Button
                                type="link"
                                onClick={handleResendOTP}
                                style={{ padding: '0', height: 'auto', fontSize: '14px' }}
                            >
                                Resend code
                            </Button>
                        )}
                    </p>
                </div>
            </div>
        </div>
    );
};

function Signin(props) {
    const { users } = useAppData();
    const navigate = useNavigate();

    // Input states
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [otpConsent, setOtpConsent] = useState(false);
    const [emailOtpConsent, setEmailOtpConsent] = useState(false);

    // Phone state
    const [country] = useState(DEFAULT_COUNTRY);
    const [phoneValid, setPhoneValid] = useState(false);

    // Auth mode states
    const [authMode, setAuthMode] = useState('phone'); // 'phone' or 'email'
    const [showPasswordInput, setShowPasswordInput] = useState(false);
    const [forgotPassword, setForgotPassword] = useState(false);

    // OTP states
    const [showOTPInput, setShowOTPInput] = useState(false);
    const [otpValue, setOtpValue] = useState([]);
    const [otpError, setOtpError] = useState('');
    const [otpSentTo, setOtpSentTo] = useState('');
    const [otpType, setOtpType] = useState(''); // 'phone' or 'email'
    const [resendTimer, setResendTimer] = useState(0);
    const [verifying, setVerifying] = useState(false);
    const [otpMessage, setOtpMessage] = useState('');
    
    // Store the redirect URL in session storage when the component mounts
    useEffect(() => {
        if (props.redirectRoute) {
            sessionStorage.setItem('authRedirect', props.redirectRoute);
        }
    }, [props.redirectRoute]);

    // Lock the body scroll when the modal is open
    useEffect(() => {
        document.body.style.overflow = props.trigger ? 'hidden' : '';
        return () => {
            document.body.style.overflow = '';
        };
    }, [props.trigger]);

    // Timer for OTP resend
    useEffect(() => {
        let interval;
        if (resendTimer > 0) {
            interval = setInterval(() => {
                setResendTimer(prev => prev - 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [resendTimer]);

    // Check if there are quiz preferences to save
    const hasQuizPreferencesToSave = () => {
        return !!localStorage.getItem('quizPreferences');
    };

    // Handle post-authentication redirect
    const handleRedirectAfterAuth = async () => {
        // Check if there are quiz preferences to save
        const shouldSaveQuizPrefs = hasQuizPreferencesToSave();
        
        try {
            if (shouldSaveQuizPrefs) {
                await saveQuizPreferences();
            }
            
            // Note: We no longer need to check for quiz completion here
            // The centralized useUserPrompts hook will handle this

            // Reset session storage prompt flag to allow showing prompts after login
            sessionStorage.removeItem('prompt_shown_this_session');
            
            // Get redirect path from session storage
            const redirectPath = sessionStorage.getItem('authRedirect');
            
            // Call onSuccess callback if it exists
            if (props.onSuccess) {
                props.onSuccess();
            }
            
            // Redirect or close modal
            if (redirectPath) {
                sessionStorage.removeItem('authRedirect');
                navigate(redirectPath);
                // Add a small delay before reload to ensure navigation completes
                setTimeout(() => window.location.reload(), 100);
            } else if (props.setTrigger) {
                props.setTrigger(false);
                // Refresh the page after closing the modal
                setTimeout(() => window.location.reload(), 100);
            }
        } catch (error) {
            console.error('Error in post-auth handling:', error);
            ConfigProvider.config({
                theme: {
                    components: {
                        Notification: {
                            titleFontSize: 20,
                        },
                    },
                },
            });
            notification.error({
                message: 'Authentication Error',
                description: 'There was a problem completing your sign-in process.',
            });
        }
    };
    
    // Save quiz preferences to the user account
    const saveQuizPreferences = async () => {
        try {
            const quizPrefs = JSON.parse(localStorage.getItem('quizPreferences'));
            if (!quizPrefs) return;

            const response = await axios.post(
                `${api}user/quiz-response`,
                { quizData: quizPrefs },
                {
                    withCredentials: true,
                    headers: {
                        'x-api-key': process.env.REACT_APP_UMMAHFY_API_KEY,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                ConfigProvider.config({
                    theme: {
                        components: {
                            Notification: {
                                titleFontSize: 20,
                            },
                        },
                    },
                });
                notification.success({
                    message: 'Preferences Saved',
                    description: 'Your quiz preferences have been saved to your account!',
                });
                
                // Clear stored preferences since they're now saved to the account
                localStorage.removeItem('quizPreferences');
            }
        } catch (error) {
            console.error('Failed to save quiz preferences:', error);
            ConfigProvider.config({
                theme: {
                    components: {
                        Notification: {
                            titleFontSize: 20,
                        },
                    },
                },
            });
            notification.error({
                message: 'Error Saving Preferences',
                description: 'We were unable to save your quiz preferences. You can try again later.',
            });
        }
    };

    // Handle user signin via email/password
    async function handleSignin(email, password, setError) {
        try {
            const data = { password };
            const response = await axios.post(`${api}users/${email}`, data, {
                withCredentials: true,
                headers: {
                    'x-api-key': process.env.REACT_APP_UMMAHFY_API_KEY,
                    'Content-Type': 'application/json'
                }
            });
            if (response.data.message === 'Authentication successful') {
                if (response.data.data && response.data.data.user) {
                    localStorage.setItem('currentUser', JSON.stringify(response.data.data.user));
                    if (response.data.data.organization) {
                        localStorage.setItem('defaultOrganization', JSON.stringify(response.data.data.organization));
                    }
                }
                
                // Handle redirect after successful authentication
                handleRedirectAfterAuth();
            } else {
                setError('Authentication failed. Please check your credentials.');
            }
        } catch (error) {
            console.error('Error:', error);
            if (error.response && error.response.data.message === 'Invalid password') {
                setError('Incorrect password');
            } else if (error.response && error.response.data.message === 'User not found') {
                setError('No user found with this email');
            } else {
                setError('An error occurred. Please try again.');
            }
        }
    }

    // Update the phone state as the user types and validate it
    const handlePhoneChange = (e) => {
        const inputValue = e.target.value;
        const filteredValue = inputValue.replace(/[^\d+\s]/g, '');
        const formattedNumber = formatPhoneNumber(filteredValue, country);
        setPhone(formattedNumber);
        const isValid = validatePhoneNumber(formattedNumber, country);
        setPhoneValid(isValid);
    };

    const isPhoneValid = (phoneNumber) => {
        return phoneNumber.trim() && validatePhoneNumber(phoneNumber, country);
    };

    // For testing - hardcoded OTP
    // MOCK_OTP is removed as we're now using real OTP functionality

    // Function for sending an OTP to the phone
    async function handlePhoneOTP(phoneNumber) {
        // Validate phone number
        if (!validatePhoneNumber(phoneNumber)) {
            setError("Please enter a valid phone number");
            return;
        }

        try {
            setError("");

            // Show loading indicator
            setResendTimer(-1); // -1 indicates sending in progress

            // Format phone to E.164 standard for sending to API
            const formattedPhone = standardizePhoneNumber(phoneNumber);

            // Call the generateOTP endpoint
            const response = await axios.post(
                `${api}otp/generateOTP`,
                {
                    identifier: formattedPhone,
                    type: 'phone'
                },
                {
                    headers: {
                        'x-api-key': process.env.REACT_APP_UMMAHFY_API_KEY,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data && response.data.data) {
                // Show OTP input screen
                setOtpType('phone');
                setOtpSentTo(formattedPhone);
                setOtpValue([]);
                setOtpError('');
                setShowOTPInput(true);

                // Start resend timer
                setResendTimer(30);
                
                // Display a masked version of the phone for enhanced privacy
                const maskedPhone = response.data.data.maskedIdentifier || formattedPhone;
                setOtpSentTo(formattedPhone); // We still need to keep the actual phone for verification
                
                // Show success message
                setOtpMessage(`A verification code has been sent to ${maskedPhone}. It will expire in 10 minutes.`);
            } else {
                setError("Failed to send verification code. Please try again.");
            }
        } catch (error) {
            console.error("Error sending phone OTP:", error);

            // Reset the sending indicator
            setResendTimer(0);

            // Enhanced error handling based on server response
            if (error.response) {
                const { status, data } = error.response;
                
                if (status === 400) {
                    // Handle validation errors
                    if (data.error === 'INVALID_PHONE_FORMAT') {
                        setError("Invalid phone number format. Please use international format (e.g., +1234567890).");
                    } else {
                        setError(data.message || "Error sending verification code. Please check your phone number.");
                    }
                } else if (status === 429) {
                    // Rate limiting
                    setError("Too many verification attempts. Please try again later.");
                } else if (status >= 500) {
                    // Server errors
                    setError("We're experiencing technical difficulties. Please try again later.");
                } else {
                    // Other errors
                    setError(data?.message || "Error sending verification code. Please try again.");
                }
            } else if (error.request) {
                // Network error
                setError("Network error. Please check your connection and try again.");
            } else {
                // Other unexpected errors
                setError("An unexpected error occurred. Please try again.");
            }
        }
    }

    // Function for sending an OTP to the email
    async function handleEmailOTP(emailAddress) {
        // Simple email format validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(emailAddress)) {
            setError("Please enter a valid email address");
            return;
        }

        try {
            setError("");

            // Show loading indicator
            setResendTimer(-1); // -1 indicates sending in progress

            // Call the generateOTP endpoint
            const response = await axios.post(
                `${api}otp/generateOTP`,
                {
                    identifier: emailAddress,
                    type: 'email'
                },
                {
                    headers: {
                        'x-api-key': process.env.REACT_APP_UMMAHFY_API_KEY,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data && response.data.data) {
                // Show OTP input screen
                setOtpType('email');
                setOtpSentTo(emailAddress);
                setOtpValue([]);
                setOtpError('');
                setShowOTPInput(true);

                // Start resend timer
                setResendTimer(60);
                
                // Display a masked version of the email for enhanced privacy
                const maskedEmail = response.data.data.maskedIdentifier || emailAddress;
                
                // Show success message
                setOtpMessage(`A verification code has been sent to ${maskedEmail}. It will expire in 10 minutes.`);
            } else {
                setError("Failed to send verification code. Please try again.");
            }
        } catch (error) {
            console.error("Error sending email OTP:", error);

            // Reset the sending indicator
            setResendTimer(0);

            // Enhanced error handling based on server response
            if (error.response) {
                const { status, data } = error.response;
                
                if (status === 400) {
                    // Handle validation errors
                    if (data.error === 'INVALID_EMAIL_FORMAT') {
                        setError("Invalid email address format. Please check and try again.");
                    } else {
                        setError(data.message || "Error sending verification code. Please check your email address.");
                    }
                } else if (status === 429) {
                    // Rate limiting
                    setError("Too many verification attempts. Please try again later.");
                } else if (status >= 500) {
                    // Server errors
                    setError("We're experiencing technical difficulties. Please try again later.");
                } else {
                    // Other errors
                    setError(data?.message || "Error sending verification code. Please try again.");
                }
            } else if (error.request) {
                // Network error
                setError("Network error. Please check your connection and try again.");
            } else {
                // Other unexpected errors
                setError("An unexpected error occurred. Please try again.");
            }
        }
    }

    // Handler for Google login
    async function handleGoogleLogin() {
        const userAgent = navigator.userAgent || navigator.vendor || window.opera;
        if (userAgent.match(/Instagram/i)) {
            const currentUrl = encodeURIComponent(window.location.href);
            navigate(`/redirect/?url=${currentUrl}`)
        } else {
            // Store any redirect URL before going to Google auth
            const redirectRoute = sessionStorage.getItem('authRedirect');
            
            // Add redirect parameter to Google auth URL if we have one
            let googleAuthUrl = api + "auth/google";
            if (redirectRoute) {
                googleAuthUrl += `?redirect=${encodeURIComponent(redirectRoute)}`;
            }
            
            window.location.href = googleAuthUrl;
        }
    }

    // Verifies the OTP entered by the user
    const handleVerifyOTP = async (code) => {
        // If code is an array (from InputOTP component), join it
        const otpCode = Array.isArray(code) ? code.join('') : code;

        if (!otpCode || otpCode.length !== 6) {
            setOtpError("Please enter all 6 digits of the code");
            return;
        }

        try {
            setVerifying(true);
            setOtpError('');

            // Call the verifyOTP endpoint
            const response = await axios.post(
                `${api}otp/verifyOTP`,
                {
                    identifier: otpSentTo,
                    code: otpCode,
                    name: null // Optional parameter for account creation
                },
                {
                    withCredentials: true, // Important: ensure cookies are sent/received
                    headers: {
                        'x-api-key': process.env.REACT_APP_UMMAHFY_API_KEY,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data && response.data.data && response.data.data.verified) {
                // Handle session error cases but still allow the user to proceed
                if (response.data.data.sessionError) {
                    console.warn("Session error during login. User may need to log in again.");
                    // We'll still continue with the login process
                }
                
                // OTP verified successfully - The user should now be logged in via session
                console.log("OTP verification successful", response.data);

                // Store user info in localStorage for frontend access
                if (response.data.data.user) {
                    localStorage.setItem('currentUser', JSON.stringify(response.data.data.user));

                    if (response.data.data.defaultOrganization) {
                        localStorage.setItem(
                            'defaultOrganization',
                            JSON.stringify(response.data.data.defaultOrganization)
                        );
                        console.log("Saved default organization to localStorage:", response.data.data.defaultOrganization);
                    } else {
                        console.warn("No default organization in the response");
                    }

                    // Check if this is a new user
                    if (response.data.data.isNewUser) {
                        // New user was created, close modal
                        props.setTrigger(false);
                        // Handle redirect
                        handleRedirectAfterAuth();
                    } else {
                        // Existing user, close modal
                        props.setTrigger(false);
                        // Handle redirect
                        handleRedirectAfterAuth();
                    }

                    // Show success feedback
                    ConfigProvider.config({
                        theme: {
                            components: {
                                Notification: {
                                    titleFontSize: 20,
                                },
                            },
                        },
                    });
                    notification.success({
                        message: 'Success',
                        description: 'Verification successful!',
                    });
                    
                    return;
                }

                // As a fallback, if no user object is in the response, check if the user exists
                if (otpType === 'phone') {
                    // For phone verification
                    try {
                        const userResponse = await axios.post(
                            `${api}users/phone/${otpSentTo}`,
                            {},
                            {
                                withCredentials: true,
                                headers: {
                                    'x-api-key': process.env.REACT_APP_UMMAHFY_API_KEY,
                                    'Content-Type': 'application/json'
                                }
                            }
                        );

                        if (userResponse.data.exists) {
                            // User exists, show password input
                            setPhone(otpSentTo);
                            setShowOTPInput(false);
                            setShowPasswordInput(true);
                        } else {
                            // New user, redirect to signup
                            navigate('/signup', { state: { phone: otpSentTo, verified: true } });
                            props.setTrigger(false);
                        }
                    } catch (userCheckError) {
                        console.error("Error checking if phone user exists:", userCheckError);
                        setOtpError("Verification succeeded, but we couldn't determine if you have an account. Please try again.");
                    }
                } else { // Email verification
                    try {
                        const userResponse = await axios.post(
                            `${api}users/email/${otpSentTo}`,
                            {},
                            {
                                withCredentials: true,
                                headers: {
                                    'x-api-key': process.env.REACT_APP_UMMAHFY_API_KEY,
                                    'Content-Type': 'application/json'
                                }
                            }
                        );

                        if (userResponse.data.exists) {
                            // User exists, show password input
                            setEmail(otpSentTo);
                            setShowOTPInput(false);
                            setShowPasswordInput(true);
                        } else {
                            // New user, redirect to signup
                            navigate('/signup', { state: { email: otpSentTo, verified: true } });
                            props.setTrigger(false);
                        }
                    } catch (userCheckError) {
                        console.error("Error checking if email user exists:", userCheckError);
                        setOtpError("Verification succeeded, but we couldn't determine if you have an account. Please try again.");
                    }
                }
            } else {
                setOtpError(response.data?.message || "Invalid verification code. Please try again.");
            }
        } catch (error) {
            console.error("Error verifying OTP:", error);
            
            // Handle specific error codes from the server
            if (error.response) {
                const { status, data } = error.response;
                
                if (status === 400) {
                    // Handle validation errors
                    if (data.error === 'INVALID_OTP') {
                        setOtpError(`${data.message || "Invalid verification code."} Please try again.`);
                    } else if (data.error === 'OTP_EXPIRED') {
                        setOtpError("Your verification code has expired. Please request a new one.");
                        setResendTimer(0); // Reset timer to allow immediate resend
                    } else if (data.error === 'MAX_ATTEMPTS_EXCEEDED') {
                        setOtpError("Too many failed attempts. Please request a new code.");
                        setResendTimer(0); // Reset timer to allow immediate resend
                    } else if (data.error === 'OTP_NOT_FOUND') {
                        setOtpError("Verification code not found. Please request a new one.");
                        setResendTimer(0); // Reset timer to allow immediate resend
                    } else {
                        setOtpError(data.message || "Error verifying code. Please try again.");
                    }
                } else if (status === 401) {
                    // Handle authentication errors
                    setOtpError("Authentication error. Please ensure you have a valid session and try again.");
                } else if (status === 429) {
                    // Rate limiting
                    setOtpError("Too many attempts. Please try again later.");
                } else if (status >= 500) {
                    // Server errors
                    setOtpError("We're experiencing technical difficulties. Please try again later.");
                } else {
                    // Other errors
                    setOtpError(data?.message || "Error verifying code. Please try again.");
                }
            } else if (error.request) {
                // Network error
                setOtpError("Network error. Please check your connection and try again.");
            } else {
                // Other unexpected errors
                setOtpError("An unexpected error occurred. Please try again.");
            }
        } finally {
            setVerifying(false);
        }
    };

    // Handler for resending the OTP code
    const handleResendOTP = async () => {
        setResendTimer(-1); // -1 indicates sending in progress

        try {
            if (otpType === 'phone') {
                await handlePhoneOTP(otpSentTo);
            } else if (otpType === 'email') {
                await handleEmailOTP(otpSentTo);
            }
        } catch (error) {
            console.error("Error resending OTP:", error);
            setOtpError("Failed to resend verification code. Please try again.");
            setResendTimer(0); // Reset timer to allow immediate retry
        }
    };

    // Handler for going back from the OTP screen
    const handleOTPGoBack = () => {
        setOtpValue([]);
        setOtpError('');
        setError('');
        setResendTimer(0);
        setShowOTPInput(false);
    };

    // Handler for closing the modal and resetting state
    const handleClose = () => {
        setError('');
        props.setTrigger(false);
        setAuthMode('phone');
        setShowPasswordInput(false);
        setPhone('');
        setEmail('');
        setPassword('');
        setShowOTPInput(false);
        setOtpValue([]);
        setOtpError('');
        setOtpSentTo('');
        setOtpType('');
        setOtpConsent(false);
        setEmailOtpConsent(false);
        setResendTimer(0);
        setVerifying(false);
        setOtpMessage('');
        
        // Call onCancel callback if it exists
        if (props.onCancel) {
            props.onCancel();
        }
    };

    // Email input change handler
    const handleEmailChange = (e) => {
        setEmail(e.target.value);
        setPassword('');
        setShowPasswordInput(false);
    };

    // Handle form submission: either phone OTP or email sign in
    const handleSubmit = async (event) => {
        event.preventDefault();
        if (authMode === 'phone') {
            if (!phone.trim()) {
                setError('Please enter a phone number');
                return;
            }
            if (!isPhoneValid(phone)) {
                setError('Please enter a valid phone number');
                return;
            }
            if (!otpConsent) {
                setError('Please consent to receive verification code');
                return;
            }
            handlePhoneOTP(phone);
        } else if (authMode === 'email') {
            if (!email.trim()) {
                setError('Please enter an email address');
                return;
            }

            try {
                // Check if user exists and has password set by querying the server
                const userCheckResponse = await axios.post(
                    `${api}users/email/${email}`,
                    {},
                    {
                        withCredentials: true,
                        headers: {
                            'x-api-key': process.env.REACT_APP_UMMAHFY_API_KEY,
                            'Content-Type': 'application/json'
                        }
                    }
                );

                // If user exists AND has password set, proceed with password login
                if (userCheckResponse.data.exists && userCheckResponse.data.hasPassword) {
                    if (showPasswordInput) {
                        if (!password.trim()) {
                            setError('Please enter your password');
                            return;
                        }
                        handleSignin(email, password, setError);
                    } else {
                        setShowPasswordInput(true);
                        return;
                    }
                } else {
                    // User doesn't exist OR user exists but doesn't have password set
                    // Use OTP flow in either case but verify consent
                    if (!emailOtpConsent) {
                        setError('Please consent to receive verification code');
                        return;
                    }
                    handleEmailOTP(email);
                }
            } catch (error) {
                console.error('Error checking user existence:', error);
                // If there's an error checking user existence, default to OTP flow with consent
                if (!emailOtpConsent) {
                    setError('Please consent to receive verification code');
                    return;
                }
                handleEmailOTP(email);
            }
        }
    };

    return (
        <>
            <ForgotPassword userType="User" trigger={forgotPassword} setTrigger={setForgotPassword} />
            <ConfigProvider
                theme={{
                    components: {
                        Modal: {},
                    },
                }}
            >
                <Modal
                    open={props.trigger}
                    onCancel={handleClose}
                    footer={null}
                    className="signin-modal"
                    centered
                    title={
                        <div className="sign-title">
                            <p>{showOTPInput ? 'Verify Code' : 'Sign In or Sign Up'}</p>
                        </div>
                    }
                >
                    {showOTPInput ? (
                        <OTPInputScreen
                            otpType={otpType}
                            otpSentTo={otpSentTo}
                            otpValue={otpValue}
                            setOtpValue={setOtpValue}
                            otpError={otpError}
                            otpMessage={otpMessage}
                            verifying={verifying}
                            resendTimer={resendTimer}
                            handleOTPGoBack={handleOTPGoBack}
                            handleVerifyOTP={handleVerifyOTP}
                            handleResendOTP={handleResendOTP}
                        />
                    ) : (
                        <form onSubmit={handleSubmit}>
                            {authMode === 'phone' ? (
                                <div className="inputs">
                                    <label htmlFor="phone">Phone Number</label>
                                    <input
                                        required
                                        type="tel"
                                        id="phone"
                                        name="phone"
                                        className={`long-input ${phone.trim() && !phoneValid ? 'invalid' : ''}`}
                                        value={phone}
                                        onChange={handlePhoneChange}
                                        placeholder={getPlaceholderNumber(country)}
                                    />
                                    <div className="consent-checkbox" style={{
                                        margin: '12px 0',
                                        display: 'flex',
                                        alignItems: 'flex-start'
                                    }}>
                                        <Checkbox
                                            checked={otpConsent}
                                            onChange={(e) => setOtpConsent(e.target.checked)}
                                            style={{ marginRight: '8px', marginTop: '3px' }}
                                        />
                                        <span 
                                            onClick={() => setOtpConsent(!otpConsent)}
                                            style={{
                                                fontSize: '12px',
                                                color: '#fff',
                                                lineHeight: '1.4',
                                                cursor: 'pointer',
                                                userSelect: 'none'
                                            }}
                                        >
                                            I agree to receive a one time passcode and marketing messages from Ummahfy at the phone number provided above. Data rates may apply, reply STOP to opt out.                                        </span>
                                    </div>
                                    {phone.trim() && !phoneValid && (
                                        <p className="error-hint" style={{
                                            fontSize: '12px',
                                            color: '#f5222d',
                                            margin: '4px 0 0 0',
                                        }}>
                                            Please enter a valid phone number with country code
                                        </p>
                                    )}
                                </div>
                            ) : (
                                <>
                                    <div className="inputs">
                                        <label htmlFor="email">Email</label>
                                        <input
                                            required
                                            type="email"
                                            id="email"
                                            name="email"
                                            className="long-input"
                                            value={email}
                                            onChange={handleEmailChange}
                                            placeholder="Enter your email"
                                        />
                                        {!showPasswordInput && (
                                            <div className="consent-checkbox" style={{
                                                margin: '12px 0',
                                                display: 'flex',
                                                alignItems: 'flex-start'
                                            }}>
                                                <Checkbox
                                                    checked={emailOtpConsent}
                                                    onChange={(e) => setEmailOtpConsent(e.target.checked)}
                                                    style={{ marginRight: '8px', marginTop: '3px' }}
                                                />
                                                <span 
                                                    onClick={() => setEmailOtpConsent(!emailOtpConsent)}
                                                    style={{
                                                        fontSize: '12px',
                                                        color: '#fff',
                                                        lineHeight: '1.4',
                                                        cursor: 'pointer',
                                                        userSelect: 'none'
                                                    }}
                                                >
                                                    By checking this box, I agree to receive a verification code via email to log in to Ummahfy.
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    {showPasswordInput && (
                                        <div className="inputs">
                                            <label htmlFor="password">Password</label>
                                            <input
                                                required
                                                type="password"
                                                id="password"
                                                name="password"
                                                className="long-input"
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                            />
                                        </div>
                                    )}
                                    {showPasswordInput && (
                                        <a className="forgot-password"
                                            onClick={() => {
                                                setForgotPassword(true);
                                                props.setTrigger(false);
                                            }}
                                        >
                                            Forgot Password?
                                        </a>
                                    )}
                                </>
                            )}

                            {error && (
                                <Alert
                                    message={error}
                                    type="error"
                                    showIcon
                                    style={{
                                        maxHeight: '42px',
                                        marginTop: '8px',
                                        borderRadius: '4px',
                                    }}
                                    closable
                                    onClose={() => setError('')}
                                />
                            )}

                            <button type="submit" className="signin">
                                {authMode === 'phone' ? 'Continue with Phone' : (showPasswordInput ? 'Sign In' : 'Continue with Email')}
                            </button>

                            <ConfigProvider
                                theme={{
                                    components: {
                                        Divider: {
                                            marginLG: 4,
                                            color: '#fff',
                                            borderColor: '#fff',
                                        }
                                    }
                                }}
                            >
                                <Divider className="or-divider">
                                    <span className="divider-text">OR</span>
                                </Divider>
                            </ConfigProvider>

                            {authMode === 'phone' ? (
                                <div className="secondary-options">
                                    <button
                                        type="button"
                                        className="signin secondary"
                                        onClick={() => setAuthMode('email')}
                                    >
                                        Login with Email
                                    </button>
                                    <button
                                        type="button"
                                        className="signin google"
                                        onClick={handleGoogleLogin}
                                        style={{ marginTop: '8px', marginBottom: '15px' }}
                                    >
                                        <GoogleLogo className="google-logo" />
                                        Continue with Google
                                    </button>
                                </div>
                            ) : (
                                <div className="secondary-options">
                                    <button
                                        type="button"
                                        className="signin secondary"
                                        onClick={() => setAuthMode('phone')}
                                    >
                                        Login with Phone
                                    </button>
                                    <button
                                        type="button"
                                        className="signin google"
                                        onClick={handleGoogleLogin}
                                        style={{ marginTop: '8px', marginBottom: '15px' }}
                                    >
                                        <GoogleLogo className="google-logo" />
                                        Continue with Google
                                    </button>
                                </div>
                            )}

                            {/* <p className="input-hint" style={{
                                fontSize: '12px',
                                color: '#888',
                                margin: '4px 0 0 0',
                                fontStyle: 'italic',
                                textAlign: 'center',
                                lineHeight: '1.3'
                            }}>
                                By signing up, you agree to receive weekly SMS event recommendations and important updates from Ummahfy.
                            </p> */}

                            <div className="policy-links" style={{
                                textAlign: 'center',
                                marginTop: '8px',
                                // paddingTop: '15px',
                                fontSize: '12px',
                                color: '#999'
                            }}>
                                <a href="/privacy" target="_blank" style={{ color: '#fff', textDecoration: 'none' }}>
                                    Privacy Policy
                                </a>
                                <span style={{ margin: '0 8px' }}>|</span>
                                <a href="/terms" target="_blank" style={{ color: '#fff', textDecoration: 'none' }}>
                                    Terms of Service
                                </a>
                            </div>
                        </form>
                    )}
                </Modal>
            </ConfigProvider>
        </>
    );
}

export default Signin;
